// Combined Design step (Tranche D). Renders all flow.questions on a single scrollable
// screen with numbered sections instead of the old q0/q1/q2 per-question step flow.
// The per-question screen components are reused with `hideHeader` so the section wrapper
// owns the title. Section content stays type-specific (openlist / location / narrow / freetext).
import {
  Button,
  Collapse,
  Group,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconTrash } from '@tabler/icons-react';
import FreetextScreen from './FreetextScreen';
import HelpScreen from './HelpScreen';
import LocationScreen from './LocationScreen';
import NarrowFiltersScreen from './NarrowFiltersScreen';
import OpenListScreen from './OpenListScreen';
import PriorityCompaniesSection, { shouldShowPriorityCompanies } from './PriorityCompaniesSection';
import { useMemo, useState } from 'react';

const DEFAULT_MAX_TOUCHES = 6;
const DEFAULT_PER_EPISODE_BUDGET = 10;
const DEFAULT_SILENCE_DAYS = 14;
const DEFAULT_EXPLORE_SHARE = 0.3;
const DEFAULT_REPLY_CONFIDENCE_THRESHOLD = 0.8;
const WEEKDAY_OPTIONS = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

function renderQuestion(question, value, onChange) {
  if (!question) return null;
  const common = { question, value, onChange, hideHeader: true };
  if (question.type === 'openlist') return <OpenListScreen {...common} />;
  if (question.type === 'location') return <LocationScreen {...common} />;
  if (question.type === 'narrowfilters') return <NarrowFiltersScreen {...common} />;
  if (question.type === 'freetext') return <FreetextScreen {...common} />;
  if (question.type === 'pickone_editable') return <HelpScreen {...common} />;
  return <Text c="red" size="sm">Unknown question type: {question.type}</Text>;
}

export default function CombinedDesignScreen({ flow, answers = {}, onAnswerChange, api }) {
  if (!flow) return null;
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const questions = Array.isArray(flow.questions) ? flow.questions : [];
  const showPriorityCompanies = shouldShowPriorityCompanies(answers);
  const bookingDays = Array.isArray(answers.bookingDays) ? answers.bookingDays : [];
  const bookingDaysError = useMemo(() => {
    const weekdays = bookingDays.map((row) => String(row?.weekday || '').toLowerCase()).filter(Boolean);
    if (new Set(weekdays).size !== weekdays.length) return 'Booking days cannot repeat the same weekday.';
    if (bookingDays.some((row) => Number(row?.endHour) <= Number(row?.startHour))) {
      return 'Each booking day must have endHour greater than startHour.';
    }
    return null;
  }, [bookingDays]);

  return (
    <Stack gap="xl">
      <Stack gap={6}>
        <Text
          component="h2"
          fw={700}
          style={{ color: 'var(--aviary-ink)', fontSize: 28, lineHeight: 1.25, margin: 0 }}
        >
          {flow.welcome?.title || "Let's set this up."}
        </Text>
        {flow.welcome?.body && (
          <Text size="md" style={{ color: 'var(--aviary-muted)', lineHeight: 1.5 }}>
            {flow.welcome.body}
          </Text>
        )}
      </Stack>

      {questions.map((question, index) => (
        <Stack key={question.key} gap="xl">
          <Paper
            p="lg"
            radius="md"
            withBorder
            style={{ background: 'var(--aviary-surface)' }}
          >
            <Stack gap="md">
              <Group gap="sm" align="baseline">
                <Text
                  fw={400}
                  style={{ color: 'var(--aviary-muted)', fontSize: 14, fontFeatureSettings: '"tnum"' }}
                >
                  {String(index + 1).padStart(2, '0')}
                </Text>
                <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                  <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 18, lineHeight: 1.3 }}>
                    {question.ask}
                  </Text>
                  {question.sub && (
                    <Text size="xs" style={{ color: 'var(--aviary-muted)', lineHeight: 1.45 }}>
                      {question.sub}
                    </Text>
                  )}
                </Stack>
              </Group>
              {renderQuestion(question, answers[question.key], (value) => onAnswerChange(question.key, value))}
            </Stack>
          </Paper>

          {question.key === 'industries' && showPriorityCompanies && (
            <Paper
              p="lg"
              radius="md"
              withBorder
              style={{ background: 'var(--mantine-color-sage-0, #eef4f0)' }}
            >
              <PriorityCompaniesSection
                api={api}
                answers={answers}
                value={answers.priorityCompanies || []}
                onChange={(value) => onAnswerChange('priorityCompanies', value)}
              />
            </Paper>
          )}
        </Stack>
      ))}

      <Paper
        p="lg"
        radius="md"
        withBorder
        style={{ background: 'var(--aviary-surface)' }}
      >
        <Stack gap="md">
          <Group justify="space-between" align="center">
            <Stack gap={2}>
              <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 18 }}>Advanced strategy settings</Text>
              <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                Optional. Skip for defaults, or tune stop rules for this campaign.
              </Text>
            </Stack>
            <Button variant="light" color="sage" onClick={() => setAdvancedOpen((v) => !v)}>
              {advancedOpen ? 'Hide advanced' : 'Show advanced'}
            </Button>
          </Group>
          <Collapse in={advancedOpen}>
            <Stack gap="sm">
              <Textarea
                label="Write your goal in your own words"
                description="Shown verbatim to the Strategist. Write it like you'd tell a human assistant."
                value={typeof answers.goal === 'string' ? answers.goal : ''}
                maxLength={500}
                autosize
                minRows={3}
                onChange={(event) => onAnswerChange('goal', event.currentTarget.value)}
              />
              <Group grow align="flex-start">
                <NumberInput
                  label="Max touches per prospect"
                  min={1}
                  max={20}
                  value={Number(answers.maxTouches || DEFAULT_MAX_TOUCHES)}
                  onChange={(value) => onAnswerChange('maxTouches', Number(value || DEFAULT_MAX_TOUCHES))}
                />
                <NumberInput
                  label="Per-episode budget"
                  min={1}
                  max={50}
                  description="How many actions the Strategist can take per prospect before stopping."
                  value={Number(answers.perEpisodeBudget || DEFAULT_PER_EPISODE_BUDGET)}
                  onChange={(value) => onAnswerChange('perEpisodeBudget', Number(value || DEFAULT_PER_EPISODE_BUDGET))}
                />
              </Group>
              <Group grow align="flex-start">
                <NumberInput
                  label="Silence days"
                  min={1}
                  max={90}
                  description="After this many days of no reply, mark the conversation dormant."
                  value={Number(answers.silenceDays || DEFAULT_SILENCE_DAYS)}
                  onChange={(value) => onAnswerChange('silenceDays', Number(value || DEFAULT_SILENCE_DAYS))}
                />
                <DateInput
                  label="End date"
                  description="Campaign auto-closes on this date."
                  clearable
                  valueFormat="YYYY-MM-DD"
                  value={answers.endAt ? new Date(answers.endAt) : null}
                  onChange={(value) => {
                    if (!value) {
                      onAnswerChange('endAt', null);
                      return;
                    }
                    const y = value.getFullYear();
                    const m = String(value.getMonth() + 1).padStart(2, '0');
                    const d = String(value.getDate()).padStart(2, '0');
                    onAnswerChange('endAt', `${y}-${m}-${d}`);
                  }}
                />
              </Group>
              <Group grow align="flex-start">
                <NumberInput
                  label="Explore share (%)"
                  min={0}
                  max={100}
                  description="Fraction of episodes the Strategist uses to try new rules."
                  value={Math.round(Number(answers.exploreShare ?? DEFAULT_EXPLORE_SHARE) * 100)}
                  onChange={(value) => onAnswerChange('exploreShare', Number(value || 0) / 100)}
                />
                <NumberInput
                  label="Reply confidence threshold (%)"
                  min={0}
                  max={100}
                  description="Minimum confidence before Jev auto-classifies a reply."
                  value={Math.round(Number(answers.replyConfidenceThreshold ?? DEFAULT_REPLY_CONFIDENCE_THRESHOLD) * 100)}
                  onChange={(value) => onAnswerChange('replyConfidenceThreshold', Number(value || 0) / 100)}
                />
              </Group>
              <Stack gap="xs">
                <Text fw={600} size="sm" style={{ color: 'var(--aviary-ink)' }}>Booking days</Text>
                {bookingDays.map((row, index) => (
                  <Group key={`${row.weekday}-${index}`} align="flex-end" grow>
                    <Select
                      label="Weekday"
                      data={WEEKDAY_OPTIONS}
                      value={row.weekday}
                      onChange={(value) => {
                        const next = [...bookingDays];
                        next[index] = { ...next[index], weekday: value || '' };
                        onAnswerChange('bookingDays', next);
                      }}
                    />
                    <NumberInput
                      label="Start hour"
                      min={0}
                      max={23}
                      value={Number(row.startHour || 0)}
                      onChange={(value) => {
                        const next = [...bookingDays];
                        next[index] = { ...next[index], startHour: Number(value || 0) };
                        onAnswerChange('bookingDays', next);
                      }}
                    />
                    <NumberInput
                      label="End hour"
                      min={0}
                      max={23}
                      value={Number(row.endHour || 0)}
                      onChange={(value) => {
                        const next = [...bookingDays];
                        next[index] = { ...next[index], endHour: Number(value || 0) };
                        onAnswerChange('bookingDays', next);
                      }}
                    />
                    <Button
                      color="red"
                      variant="subtle"
                      leftSection={<IconTrash size={14} />}
                      onClick={() => {
                        onAnswerChange('bookingDays', bookingDays.filter((_, i) => i !== index));
                      }}
                    >
                      Remove
                    </Button>
                  </Group>
                ))}
                <Button
                  variant="light"
                  color="sage"
                  onClick={() => {
                    onAnswerChange('bookingDays', [
                      ...bookingDays,
                      { weekday: 'monday', startHour: 9, endHour: 17 },
                    ]);
                  }}
                  style={{ width: 'fit-content' }}
                >
                  Add booking day
                </Button>
                {bookingDaysError && (
                  <Text size="xs" c="red">{bookingDaysError}</Text>
                )}
              </Stack>
            </Stack>
          </Collapse>
        </Stack>
      </Paper>

      <Text size="xs" style={{ color: 'var(--aviary-muted)', fontStyle: 'italic' }}>
        Saved as you go
      </Text>
    </Stack>
  );
}
